import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config';

/**
 * SPAIN_ID — España tiene id 68 en el sistema de paises.
 * Se usa para saber si mostrar/ocultar provincia y localidad.
 */
export const SPAIN_ID = 68;

/**
 * ── Módulo-level cache ──
 * Compartido entre todas las instancias del composable para evitar
 * fetches repetidos de los mismos datos.
 */
const paises = ref([]);
const provincias = ref([]);
const localidades = ref([]);
const loadedPaisId = ref(null);
const loadedProvinciaId = ref(null);

/**
 * useUbicacion — Composable para cascada País → Provincia → Localidad.
 *
 * Maneja la carga condicional:
 *   - País: siempre disponible.
 *   - Provincia: solo se carga si el país seleccionado es España.
 *   - Localidad: solo se carga si hay provincia seleccionada.
 *
 * @returns {Object}  Ref reactivas + funciones de selección.
 */
export function useUbicacion() {
    const loadingPaises = ref(false);
    const loadingProvincias = ref(false);
    const loadingLocalidades = ref(false);

    const paisId = ref(null);
    const provinciaId = ref(null);
    const localidadId = ref(null);

    /** true si el país seleccionado es España (id 68). */
    const esEspana = computed(() => paisId.value === SPAIN_ID);

    /**
     * Cargar lista de países (cacheada).
     */
    const cargarPaises = async() => {
        if (paises.value.length > 0) return;

        loadingPaises.value = true;
        try {
            const res = await store.dispatch(config.getVuexAlias('select.paises'));
            paises.value = res.data.data;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useUbicacion] Error al cargar países:', err);
            }
        } finally {
            loadingPaises.value = false;
        }
    };

    /**
     * Cargar provincias de un país (cacheado por paisId).
     * @param {Number|null} id — ID del país.
     */
    const cargarProvincias = async id => {
        if (!id) {
            provincias.value = [];
            loadedPaisId.value = null;
            return;
        }

        if (id === loadedPaisId.value && provincias.value.length > 0) return;

        loadingProvincias.value = true;
        provincias.value = [];
        try {
            const res = await store.dispatch(
                config.getVuexAlias('select.provincias'),
                { filters: { pais_id: id } },
            );
            provincias.value = res.data.data;
            loadedPaisId.value = id;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useUbicacion] Error al cargar provincias:', err);
            }
        } finally {
            loadingProvincias.value = false;
        }
    };

    /**
     * Cargar localidades de una provincia (cacheado por provinciaId).
     * @param {Number|null} id — ID de la provincia.
     */
    const cargarLocalidades = async id => {
        if (!id) {
            localidades.value = [];
            loadedProvinciaId.value = null;
            return;
        }

        if (id === loadedProvinciaId.value && localidades.value.length > 0) return;

        loadingLocalidades.value = true;
        localidades.value = [];
        try {
            const res = await store.dispatch(
                config.getVuexAlias('select.locations'),
                { filters: { provincia_id: id } },
            );
            localidades.value = res.data.data;
            loadedProvinciaId.value = id;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useUbicacion] Error al cargar localidades:', err);
            }
        } finally {
            loadingLocalidades.value = false;
        }
    };

    /**
     * Seleccionar un país. Resetea provincia y localidad.
     * Si es España, carga provincias automáticamente.
     * @param {Number} id — ID del país seleccionado.
     */
    const seleccionarPais = async id => {
        paisId.value = id;
        provinciaId.value = null;
        localidadId.value = null;
        provincias.value = [];
        localidades.value = [];

        if (id === SPAIN_ID) {
            await cargarProvincias(id);
        }
    };

    /**
     * Seleccionar una provincia. Resetea localidad y carga localidades.
     * @param {Number} id — ID de la provincia seleccionada.
     */
    const seleccionarProvincia = async id => {
        provinciaId.value = id;
        localidadId.value = null;
        localidades.value = [];
        await cargarLocalidades(id);
    };

    /**
     * Seleccionar una localidad.
     * @param {Number} id — ID de la localidad seleccionada.
     */
    const seleccionarLocalidad = id => {
        localidadId.value = id;
    };

    /**
     * Inicializar el estado con valores existentes (para edición).
     * @param {Number|null} initPaisId
     * @param {Number|null} initProvinciaId
     * @param {Number|null} initLocalidadId
     */
    const inicializar = async(initPaisId, initProvinciaId, initLocalidadId) => {
        await cargarPaises();
        paisId.value = initPaisId ?? null;

        if (initPaisId === SPAIN_ID) {
            await cargarProvincias(initPaisId);
            provinciaId.value = initProvinciaId ?? null;
        }

        if (initPaisId === SPAIN_ID && initProvinciaId) {
            await cargarLocalidades(initProvinciaId);
            localidadId.value = initLocalidadId ?? null;
        }
    };

    return {
        paises,
        provincias,
        localidades,
        loadingPaises,
        loadingProvincias,
        loadingLocalidades,
        paisId,
        provinciaId,
        localidadId,
        esEspana,
        cargarPaises,
        seleccionarPais,
        seleccionarProvincia,
        seleccionarLocalidad,
        inicializar,
    };
}
